import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useGameStore } from '@/store/gameStore';
import { DIFFICULTY_LEVELS } from '@/engine/constants';
import { Swords, Brain } from 'lucide-react';

const NewGameDialog = ({ isOpen, onClose }) => {
  const [selectedColor, setSelectedColor] = useState('white');
  const [selectedDifficulty, setSelectedDifficulty] = useState('medium');
  const { startNewGame, boardTheme } = useGameStore();

  const handleStart = () => {
    startNewGame(selectedColor, selectedDifficulty);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`
          ${boardTheme === 'cosmic'
            ? 'bg-slate-900 border-purple-500/30 text-white'
            : 'bg-amber-50 border-amber-300 text-gray-900'
          }
          max-w-md
        `}
        data-testid="new-game-dialog"
      >
        <DialogHeader>
          <DialogTitle className={`
            text-xl font-bold flex items-center gap-2
            ${boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-amber-800'}
          `}>
            <Swords className="w-6 h-6" />
            New Game
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Color Selection */}
          <div>
            <label className={`text-sm font-medium mb-3 block ${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
              Play as
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedColor('white')}
                className={`
                  flex-1 p-4 rounded-lg border-2 transition-all
                  ${selectedColor === 'white'
                    ? boardTheme === 'cosmic'
                      ? 'border-cyan-500 bg-cyan-900/30'
                      : 'border-amber-500 bg-amber-100'
                    : boardTheme === 'cosmic'
                      ? 'border-slate-700 bg-slate-800 hover:border-slate-600'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
                data-testid="select-white"
              >
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-white border-2 border-gray-300 shadow-md" />
                <span className={`font-medium ${boardTheme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                  White
                </span>
                <p className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Move first
                </p>
              </button>
              
              <button
                onClick={() => setSelectedColor('black')}
                className={`
                  flex-1 p-4 rounded-lg border-2 transition-all
                  ${selectedColor === 'black'
                    ? boardTheme === 'cosmic'
                      ? 'border-cyan-500 bg-cyan-900/30'
                      : 'border-amber-500 bg-amber-100'
                    : boardTheme === 'cosmic'
                      ? 'border-slate-700 bg-slate-800 hover:border-slate-600'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
                data-testid="select-black"
              >
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-slate-900 border-2 border-yellow-500 shadow-md" />
                <span className={`font-medium ${boardTheme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                  Black
                </span>
                <p className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
                  AI moves first
                </p>
              </button>
            </div>
          </div>

          {/* Difficulty Selection */}
          <div>
            <label className={`text-sm font-medium mb-3 flex items-center gap-2 ${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
              <Brain className="w-4 h-4" />
              AI Difficulty
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(DIFFICULTY_LEVELS).map(([key, { name, description }]) => (
                <button
                  key={key}
                  onClick={() => setSelectedDifficulty(key)}
                  className={`
                    p-3 rounded-lg border-2 text-left transition-all
                    ${selectedDifficulty === key
                      ? boardTheme === 'cosmic'
                        ? 'border-cyan-500 bg-cyan-900/30'
                        : 'border-amber-500 bg-amber-100'
                      : boardTheme === 'cosmic'
                        ? 'border-slate-700 bg-slate-800 hover:border-slate-600'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }
                  `}
                  data-testid={`difficulty-${key}`}
                >
                  <span className={`font-medium block ${boardTheme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                    {name}
                  </span>
                  <span className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {description}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className={`flex-1 ${boardTheme === 'cosmic' ? 'bg-slate-800 border-slate-600' : ''}`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStart}
            className={`
              flex-1
              ${boardTheme === 'cosmic'
                ? 'bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700'
                : 'bg-amber-600 hover:bg-amber-700'
              }
              text-white
            `}
            data-testid="start-game-btn"
          >
            Start Game
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewGameDialog;
